/**
 * История бесед ИИ на сервере (agroai-6al.16): `/ai/agent/chats*`,
 * `/ai/agent/reports*`. Беседа больше не живёт во вкладке: закрытая вкладка
 * не теряет ни ответа, ни отчёта. Незакреплённая беседа удаляется сервером
 * через `ttl_days` после последней активности, закреплённая — хранится.
 */

import type { UIMessage } from 'ai';
import { defineMessages } from '../i18n/define';
import { tr } from '../i18n/runtime';
import { clock, stamp } from '../astra/lib/format';

const m = defineMessages({
  serverStatus: { id: 'ai.history.serverStatus', defaultMessage: 'сервер ответил {status}', description: 'Причина ошибки запроса истории бесед, когда сервер не дал текста; строчными; {status} — код ответа, например 502' },
  timeout: { id: 'ai.history.timeout', defaultMessage: 'сервер не ответил вовремя', description: 'Причина ошибки запроса истории бесед: сервер не ответил за отведённое время; строчными' },
  listUnavailable: { id: 'ai.history.listUnavailable', defaultMessage: 'история недоступна', description: 'Причина ошибки: сервер вернул непонятный список бесед; строчными' },
  chatUnavailable: { id: 'ai.history.chatUnavailable', defaultMessage: 'беседа недоступна', description: 'Причина ошибки: сервер вернул непонятную беседу; строчными' },
  yesterday: { id: 'ai.history.yesterday', defaultMessage: 'вчера', description: 'Подпись даты беседы в списке (панель ИИ): вчера; коротко, строчными' },
  untitled: { id: 'ai.history.untitled', defaultMessage: 'Новая беседа', description: 'Название беседы, у которой ещё нет своего (сервер берёт его из первого вопроса), — в шапке панели ИИ и в списке бесед' },
});

/**
 * Название беседы по умолчанию на сервере (`ai_agent.history.DEFAULT_TITLE`): это знак «названия
 * ещё нет», а не текст для показа. Совпадение держит `backend/tests/test_ai_agent_panel_captions.py`.
 */
export const SERVER_UNTITLED = 'Новый чат'; // i18n-ignore: значение сервера, не строка экрана

/** Название беседы для показа: пустое и умолчание сервера — «Новая беседа» на языке интерфейса. */
export function chatTitle(title: string | null | undefined): string {
  return !title || title === SERVER_UNTITLED ? tr(m.untitled) : title;
}

const BASE = `${(import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')}/ai/agent`;

export interface ChatSummary {
  id: string;
  title: string;
  mode: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export interface ChatDetail extends ChatSummary {
  /** `running` — ответ ещё готовится на сервере (agroai-6al.58). */
  status?: 'running' | 'idle';
  elapsed_s?: number;
  messages: UIMessage[];
}

export interface ChatList {
  chats: ChatSummary[];
  ttl_days: number;
}

export interface ReportEntry {
  chat_id: string;
  tool_call_id: string;
  title: string;
  created_at: string;
  pinned: boolean;
}

export function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Потолок ожидания ответа истории: панель не висит на мёртвом сервере. */
const TIMEOUT_MS = 15_000;

export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = csrfToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${BASE}${path}`, {
      credentials: 'same-origin',
      ...init,
      signal: init.signal ?? controller.signal,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { 'X-CSRFToken': token } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    if (response.status === 204) return undefined as T;
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const detail =
        (body as { detail?: string } | null)?.detail ?? (body as { error?: { message?: string } } | null)?.error?.message;
      throw new Error(detail ?? tr(m.serverStatus, { status: response.status }));
    }
    return body as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new Error(tr(m.timeout));
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

const isList = (value: unknown): value is ChatList =>
  typeof value === 'object' && value !== null && Array.isArray((value as ChatList).chats);

export async function listChats(): Promise<ChatList> {
  const body = await call<unknown>('/chats');
  if (!isList(body)) throw new Error(tr(m.listUnavailable));
  return body;
}

export const createChat = (mode = '', title = '') =>
  call<ChatSummary>('/chats', { method: 'POST', body: JSON.stringify(title ? { mode, title } : { mode }) });

export async function getChat(id: string): Promise<ChatDetail> {
  const body = await call<ChatDetail>(`/chats/${encodeURIComponent(id)}`);
  if (!body || !Array.isArray(body.messages)) throw new Error(tr(m.chatUnavailable));
  return body;
}

export const patchChat = (id: string, patch: { title?: string; pinned?: boolean }) =>
  call<ChatSummary>(`/chats/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteChat = (id: string) => call<void>(`/chats/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** «Остановить»: сервер прекращает генерацию (флаг в базе), токены дальше не тратятся. */
export const stopChat = (id: string) => call<{ running: boolean }>(`/chats/${encodeURIComponent(id)}/stop`, { method: 'POST' });

/** «Уточнить» во время ответа: `queued: false` — ответ уже закончился. */
export const clarifyChat = (id: string, text: string) =>
  call<{ queued: boolean }>(`/chats/${encodeURIComponent(id)}/clarify`, { method: 'POST', body: JSON.stringify({ text }) });

/** 👍/👎 ответа; пустое значение снимает отметку. */
export const feedbackChat = (id: string, messageId: string, value: 'up' | 'down' | '') =>
  call<ChatSummary>(`/chats/${encodeURIComponent(id)}/feedback`, { method: 'POST', body: JSON.stringify({ message_id: messageId, value }) });

/** «Новая беседа отсюда»: копия истории до сообщения включительно. */
export const forkChat = (id: string, messageId: string) =>
  call<ChatSummary>(`/chats/${encodeURIComponent(id)}/fork`, { method: 'POST', body: JSON.stringify({ message_id: messageId }) });

export async function listReportEntries(): Promise<ReportEntry[]> {
  const body = await call<unknown>('/reports');
  return Array.isArray(body) ? (body as ReportEntry[]) : [];
}

export const importReport = (report: unknown) =>
  call<ReportEntry>('/reports/import', { method: 'POST', body: JSON.stringify({ report }) });

/** «Недавно», «вчера», «12 сент.» — подпись даты в списке бесед. */
export function chatDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(date)) / 86_400_000);
  if (diff === 0) return clock(date);
  if (diff === 1) return tr(m.yesterday);
  return stamp(date);
}
